import { Button, Icon, Input } from "@elaniintc/goes-design-system";
import { cn } from "@/utils/theme";
import type { ThreadConfig } from "./types";

interface ThreadListProps {
  threads: ThreadConfig[];
  onAddThread: () => void;
  onDeleteThread: (id: string) => void;
  onUpdateThread: (id: string, thread: ThreadConfig) => void;
  maxThreads?: number;
  minThreads?: number;
}

const ThreadList = ({
  threads = [],
  onAddThread,
  onDeleteThread,
  onUpdateThread,
  maxThreads = 10,
  minThreads = 2,
}: ThreadListProps) => {
  const canAddThread = threads.length < maxThreads;
  const canDeleteThread = threads.length > minThreads;

  const handleNewThread = () => {
    if (!canAddThread) return;

    // Validate existing threads before adding a new one
    const hasEmptyThread = threads.some((t) => !t.label.trim());
    if (hasEmptyThread) return;

    onAddThread();
  };

  const handleDeleteThread = (id: string) => {
    if (!canDeleteThread) return;
    onDeleteThread(id);
  };

  return (
    <div className="w-full flex flex-col gap-6 p-6">
      {threads.map((thread, index) => {
        return (
          <div
            key={thread.id}
            className={cn(
              "w-full grid gap-2",
              index < minThreads && "grid-cols-[1fr]",
              index >= minThreads && "grid-cols-[1fr_32px] place-items-center",
            )}
          >
            <div className="w-full flex flex-col gap-1">
              <label className="font-semibold text-body-loud text-sm leading-2xl">
                Salida {index + 1}
              </label>
              <Input
                value={thread.label}
                onChange={(e) => {
                  onUpdateThread(thread.id, {
                    ...thread,
                    label: e.target.value,
                  });
                }}
                placeholder="Escribe el nombre de la salida"
                maxLength={50}
              />
            </div>
            {index >= minThreads && (
              <div className="mt-5">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Eliminar hilo"
                  className="size-7"
                  type="button"
                  onClick={() => handleDeleteThread(thread.id)}
                  disabled={!canDeleteThread}
                >
                  <Icon type="far" name="times" />
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {canAddThread && (
        <div className="w-full flex justify-start">
          <Button
            variant="tertiary"
            onClick={handleNewThread}
            disabled={!canAddThread || threads.some((t) => !t.label.trim())}
          >
            Añadir salida
            <Icon type="far" name="plus" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ThreadList;
